/**
 * Dialog para vincular agentes a um departamento
 * 
 * Interface redesenhada com visualização clara de supervisores e agentes.
 * Supervisores são destacados visualmente e têm tooltips explicativos.
 * 
 * @module LinkAgentsDialog
 * @version 2.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, User, Star, Users, Shield, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { toast } from 'sonner';

// Interface para agente disponível (membro do tenant)
interface Agent {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

// Interface para vínculo existente
interface DepartmentAgent {
  user_id: string;
  is_supervisor: boolean;
  profile: Agent;
}

// Interface para departamento com agentes
interface Department {
  id: string;
  name: string;
  agents: DepartmentAgent[];
}

interface LinkAgentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  department: Department | null;
  tenantId: string;
  onSuccess: () => void;
}

export function LinkAgentsDialog({
  open,
  onOpenChange,
  department,
  tenantId,
  onSuccess,
}: LinkAgentsDialogProps) {
  // Estado dos agentes selecionados (Set de user_id)
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  // Estado dos supervisores (Set de user_id)
  const [supervisors, setSupervisors] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // v2.0.0: Membros do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [], isLoading: loading } = useTenantMembers({
    onlyActive: true,
    enabled: open && !!tenantId,
  });
  const availableAgents: Agent[] = tenantMembers.map(m => ({
    id: m.user_id,
    full_name: m.full_name,
    email: m.email || '',
    avatar_url: m.avatar_url,
  }));

  // Popula seleção com agentes já vinculados
  useEffect(() => {
    if (department && open) {
      const linkedIds = new Set(department.agents.map((a) => a.user_id));
      setSelectedAgents(linkedIds);

      const supervisorIds = new Set(
        department.agents.filter((a) => a.is_supervisor).map((a) => a.user_id)
      );
      setSupervisors(supervisorIds);
    }
  }, [department, open]);

  // Alterna seleção de agente
  const handleToggleAgent = (agentId: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
        // Remove também do set de supervisores se estava lá
        setSupervisors((s) => {
          const newS = new Set(s);
          newS.delete(agentId);
          return newS;
        });
      } else {
        next.add(agentId);
      }
      return next;
    });
  };

  // Alterna status de supervisor
  const handleToggleSupervisor = (agentId: string) => {
    // Garante que o agente está selecionado antes de torná-lo supervisor
    if (!selectedAgents.has(agentId)) {
      setSelectedAgents((prev) => new Set(prev).add(agentId));
    }
    
    setSupervisors((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  };

  // Server-side: uma única chamada atômica via RPC sync_department_agents
  const handleSave = async () => {
    if (!department) return;

    try {
      setSaving(true);

      const agentIds = [...selectedAgents];
      const supervisorIds = [...supervisors];

      const { error } = await supabase.rpc('sync_department_agents', {
        p_department_id: department.id,
        p_agent_user_ids: agentIds,
        p_supervisor_user_ids: supervisorIds,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;

      toast.success('Membros vinculados com sucesso');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Erro ao vincular membros:', error);
      toast.error('Erro ao vincular membros');
    } finally {
      setSaving(false);
    }
  };

  // Gera iniciais para avatar
  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Filtra agentes selecionados como apenas agentes (não supervisores)
  const selectedAgentsOnly = [...selectedAgents].filter(id => !supervisors.has(id));
  // Filtra supervisores
  const selectedSupervisors = [...supervisors];

  // Componente de card de agente reutilizável - layout compacto e premium
  const AgentCard = ({ 
    agent, 
    isSelected, 
    isSupervisor,
    onToggleSelect,
    onToggleSupervisor,
    showSupervisorToggle = true
  }: {
    agent: Agent;
    isSelected: boolean;
    isSupervisor: boolean;
    onToggleSelect: () => void;
    onToggleSupervisor: () => void;
    showSupervisorToggle?: boolean;
  }) => (
    <div
      className={`flex items-center justify-between p-2.5 rounded-lg border transition-all ${
        isSupervisor
          ? 'border-amber-500/50 bg-amber-500/5'
          : isSelected
          ? 'border-primary bg-primary/5'
          : 'hover:bg-muted/50'
      }`}
    >
      <div
        className="flex items-center space-x-2.5 flex-1 cursor-pointer"
        onClick={onToggleSelect}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelect}
          className="h-4 w-4"
        />
        <Avatar className="h-7 w-7">
          <AvatarImage src={agent.avatar_url || undefined} />
          <AvatarFallback className={`text-[10px] ${
            isSupervisor 
              ? 'bg-amber-500/20 text-amber-700' 
              : 'bg-primary/10 text-primary'
          }`}>
            {getInitials(agent.full_name, agent.email)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate">
              {agent.full_name || agent.email}
            </p>
            {isSupervisor && (
              <Badge variant="outline" className="gap-0.5 shrink-0 border-amber-500/50 text-amber-700 bg-amber-500/10 text-[10px] py-0 px-1.5 h-4">
                <Star className="h-2.5 w-2.5 fill-current" />
                Supervisor
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {agent.email}
          </p>
        </div>
      </div>

      {/* Botão para marcar como supervisor - sempre visível se selecionado */}
      {showSupervisorToggle && isSelected && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isSupervisor ? 'default' : 'outline'}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSupervisor();
                }}
                className={`ml-2 shrink-0 h-7 w-7 p-0 ${
                  isSupervisor 
                    ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                    : 'border-amber-500/50 text-amber-700 hover:bg-amber-500/10'
                }`}
              >
                <Star
                  className={`h-3.5 w-3.5 ${isSupervisor ? 'fill-current' : ''}`}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px]">
              <p className="text-xs font-medium mb-0.5">
                {isSupervisor ? 'Remover supervisão' : 'Marcar como supervisor'}
              </p>
              <p className="text-[10px] text-muted-foreground leading-tight">
                Supervisores têm visibilidade ampliada. Configure nas opções do departamento
                se devem receber conversas na distribuição.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Vincular Membros ao Departamento
          </DialogTitle>
          <DialogDescription>
            Selecione os membros que terão acesso às conversas do departamento "
            {department?.name}".
          </DialogDescription>
        </DialogHeader>

        {/* Informação sobre supervisores - compacto */}
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/50 border">
          <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="text-sm font-medium text-foreground mb-0.5">Sobre Supervisores</p>
            <p className="leading-relaxed">
              Clique no ícone <Star className="h-2.5 w-2.5 inline text-amber-500" /> para definir um membro como supervisor.
              Supervisores têm visibilidade ampliada. Configure nas opções do departamento
              se devem receber conversas na distribuição.
            </p>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-9">
            <TabsTrigger value="all" className="gap-1 text-xs">
              <Users className="h-3.5 w-3.5" />
              Todos ({availableAgents.length})
            </TabsTrigger>
            <TabsTrigger value="agents" className="gap-1 text-xs">
              <User className="h-3.5 w-3.5" />
              Agentes ({selectedAgentsOnly.length})
            </TabsTrigger>
            <TabsTrigger value="supervisors" className="gap-1 text-xs">
              <Shield className="h-3.5 w-3.5" />
              Supervisores ({selectedSupervisors.length})
            </TabsTrigger>
          </TabsList>

          {/* Aba: Todos os membros */}
          <TabsContent value="all" className="mt-3">
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availableAgents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum membro disponível no tenant.
                </p>
              ) : (
                availableAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    isSelected={selectedAgents.has(agent.id)}
                    isSupervisor={supervisors.has(agent.id)}
                    onToggleSelect={() => handleToggleAgent(agent.id)}
                    onToggleSupervisor={() => handleToggleSupervisor(agent.id)}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Aba: Apenas Agentes (não supervisores) */}
          <TabsContent value="agents" className="mt-3">
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
              {selectedAgentsOnly.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum agente selecionado.</p>
                  <p className="text-sm">
                    Selecione membros na aba "Todos" para adicioná-los.
                  </p>
                </div>
              ) : (
                selectedAgentsOnly.map((userId) => {
                  const agent = availableAgents.find((a) => a.id === userId);
                  if (!agent) return null;
                  return (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      isSelected={true}
                      isSupervisor={false}
                      onToggleSelect={() => handleToggleAgent(agent.id)}
                      onToggleSupervisor={() => handleToggleSupervisor(agent.id)}
                    />
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Aba: Supervisores */}
          <TabsContent value="supervisors" className="mt-3">
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
              {selectedSupervisors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum supervisor definido.</p>
                  <p className="text-sm">
                    Clique no ícone <Star className="h-3 w-3 inline text-amber-500" /> em um membro para defini-lo como supervisor.
                  </p>
                </div>
              ) : (
                selectedSupervisors.map((userId) => {
                  const agent = availableAgents.find((a) => a.id === userId);
                  if (!agent) return null;
                  return (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      isSelected={true}
                      isSupervisor={true}
                      onToggleSelect={() => handleToggleAgent(agent.id)}
                      onToggleSupervisor={() => handleToggleSupervisor(agent.id)}
                    />
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Resumo da seleção - compacto */}
        {selectedAgents.size > 0 && (
          <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
            <span>
              {selectedAgents.size} membro{selectedAgents.size !== 1 ? 's' : ''} selecionado{selectedAgents.size !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              <Star className="h-2.5 w-2.5 text-amber-500 fill-current" />
              {supervisors.size} supervisor{supervisors.size !== 1 ? 'es' : ''}
            </span>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
